<template>
  <div class="pa-4" style="max-width: 1100px; margin: 0 auto">
    <h2 class="mb-1">API Console</h2>
    <p class="text-caption mb-4" style="opacity: 0.7">
      Dev alat. Zahtjevi idu kroz isti axios kao aplikacija — token,
      <code>company</code> i <code>restaurant</code> header se dodaju automatski.
      URL je relativan na <code>{{ apiBase }}</code>.
    </p>

    <!-- Auth context -->
    <v-alert dense text type="info" class="mb-4">
      <div style="display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px">
        <span>token: <b>{{ hasToken ? "da" : "NE" }}</b></span>
        <span>user.type: <b>{{ userType || "—" }}</b></span>
        <span>state.company.id: <b>{{ companyId || "—" }}</b></span>
        <span>state.restaurant.id: <b>{{ restaurantId || "—" }}</b></span>
      </div>
    </v-alert>

    <!-- Presets -->
    <div class="mb-3">
      <span class="text-caption mr-2" style="opacity: 0.7">Brzi izbor:</span>
      <v-chip
        v-for="p in presets"
        :key="p.label"
        small
        outlined
        class="ma-1"
        @click="applyPreset(p)"
      >
        {{ p.label }}
      </v-chip>
    </div>

    <!-- Request builder -->
    <v-card outlined class="pa-4 mb-4">
      <div style="display: flex; gap: 12px; align-items: flex-start">
        <v-select
          v-model="method"
          :items="methods"
          label="Metoda"
          dense
          outlined
          hide-details
          style="max-width: 130px"
        />
        <v-text-field
          v-model="url"
          label="URL (relativno na /api)"
          placeholder="companies/24/users-delivery"
          dense
          outlined
          hide-details
          @keyup.enter="send"
        />
        <v-btn color="primary" :loading="loading" @click="send" style="height: 40px">
          Pošalji
        </v-btn>
      </div>

      <v-textarea
        v-if="bodyAllowed"
        v-model="body"
        label="Body (JSON)"
        placeholder='{ "name": "..." }'
        outlined
        dense
        rows="6"
        class="mt-4"
        hide-details="auto"
        :error="bodyError"
        :error-messages="bodyError ? 'Nevažeći JSON' : ''"
        spellcheck="false"
        style="font-family: monospace"
      />
    </v-card>

    <!-- Response -->
    <v-card v-if="response || errorText" outlined class="pa-4">
      <div
        v-if="response"
        class="mb-2"
        style="display: flex; gap: 16px; flex-wrap: wrap; font-size: 13px"
      >
        <span>
          status:
          <b :style="{ color: statusColor }">{{ response.status }} {{ response.statusText }}</b>
        </span>
        <span>vrijeme: <b>{{ response.ms }} ms</b></span>
        <span>veličina: <b>{{ response.size }}</b></span>
      </div>

      <v-alert v-if="errorText" dense text type="error" class="mb-2">
        {{ errorText }}
      </v-alert>

      <div style="display: flex; gap: 8px" class="mb-2">
        <v-btn x-small text @click="copy(prettyBody)">Kopiraj body</v-btn>
        <v-btn x-small text @click="showHeaders = !showHeaders">
          {{ showHeaders ? "Sakrij" : "Prikaži" }} response headers
        </v-btn>
      </div>

      <pre v-if="showHeaders" class="resp">{{ prettyHeaders }}</pre>
      <pre class="resp">{{ prettyBody }}</pre>
    </v-card>

    <!-- History -->
    <v-card v-if="history.length" outlined class="pa-4 mt-4">
      <div class="text-caption mb-2" style="opacity: 0.7">
        Istorija (ova sesija)
        <v-btn x-small text @click="history = []">očisti</v-btn>
      </div>
      <div
        v-for="(h, i) in history"
        :key="i"
        class="hist"
        @click="restore(h)"
      >
        <span :style="{ color: h.ok ? '#2e7d32' : '#c62828' }">{{ h.status }}</span>
        <b class="mx-2">{{ h.method }}</b>
        <span>{{ h.url }}</span>
        <span class="ml-2" style="opacity: 0.6">{{ h.ms }} ms</span>
      </div>
    </v-card>
  </div>
</template>

<script>
// Dev-only. Not linked from any menu — open /dev/api directly.
import axiosInstance from "@/plugins/axios";
import cfg from "@/config";

const LS_KEY = "dev-api-console:last";

export default {
  name: "DevApiConsole",
  data() {
    return {
      apiBase: cfg.apiUrl,
      methods: ["GET", "POST", "PATCH", "PUT", "DELETE"],
      method: "GET",
      url: "",
      body: "",
      loading: false,
      response: null,
      errorText: "",
      showHeaders: false,
      history: [],
      presets: [
        // Krug 2 (11.09) — vidi 11_09_2026_Network_checklist.md. Test nalog:
        // `restaurant` restorana 175 (interna firma 24). Zamijeni <ID> / <FIRM_ID>
        // stvarnim vrijednostima prije slanja.
        { label: "C1 GET firma 24", method: "GET", url: "companies/24/users-delivery" },
        {
          label: "C2 PATCH korisnik <ID>",
          method: "PATCH",
          url: "companies/24/users-delivery/<ID>",
          body: { name: "Izmjena", lastname: "Test" },
        },
        {
          // Poslije slanja, pošalji C1 ponovo i provjeri da li se contact_phone
          // pojavljuje u pivot-u tog korisnika u GET odgovoru.
          label: "C2b PATCH pivot polje <ID>",
          method: "PATCH",
          url: "companies/24/users-delivery/<ID>",
          body: { contact_phone: "060111222" },
        },
        {
          label: "C3 DELETE korisnik <ID> (aktivan)",
          method: "DELETE",
          url: "companies/24/users-delivery/<ID>",
        },
        {
          // Pošalji isti zahtjev kao C3 odmah poslije — očekuje se 409 drugi put.
          label: "C4 DELETE isti <ID> ponovo (→409)",
          method: "DELETE",
          url: "companies/24/users-delivery/<ID>",
        },
        {
          // Namjerno BEZ "state" polja — provjera da default bude 1, ne null.
          // is_transferable MORA biti poslat (11.09 nalaz: bez njega backend
          // baca sirov SQL "Column 'is_transferable' cannot be null" i zahtjev
          // potpuno propadne — vidi C5-bug niže).
          label: "C5 POST novi korisnik (bez state)",
          method: "POST",
          url: "companies/24/users-delivery",
          body: {
            name: "Test",
            lastname: "Create",
            email: "PROMIJENI@ordera",
            password: "test1234",
            phone: "060000000",
            type: "dostava",
            is_transferable: 0,
          },
        },
        {
          // Reprodukcija 11.09 buga — namjerno BEZ is_transferable. Očekuje se
          // da ovo i dalje puca dok backend ne doda server-side default.
          label: "C5-bug POST bez is_transferable (repro)",
          method: "POST",
          url: "companies/24/users-delivery",
          body: {
            name: "Test",
            lastname: "Create",
            email: "PROMIJENI@ordera",
            password: "test1234",
            phone: "060000000",
            type: "dostava",
          },
        },
        { label: "C6 GET lista firmi", method: "GET", url: "companies/companies-delivery" },
        {
          // Payload je BARE niz korisnika (kao CompanyEmployeeQuickAddDialog).
          // "Duplikat" koristi dostavljac2@ordera (id 30576) — stvaran, aktivan
          // delivery korisnik firme 24 iz C1/C6 odgovora — umjesto placeholder-a,
          // da se A4-scenario (već postojeći email) realno pogodi.
          label: "C7 group-add (mješoviti batch)",
          method: "POST",
          url: "companies/24/add-multiple-users-delivery",
          body: [
            {
              name: "Test",
              lastname: "Validan",
              email: "c7-test-validan@ordera",
              password: "test1234",
              type: "dostava",
            },
            {
              name: "Test",
              lastname: "Duplikat",
              email: "dostavljac2@ordera",
              password: "test1234",
              type: "dostava",
            },
            {
              name: "Test",
              lastname: "Losemail",
              email: "nije-email",
              password: "test1234",
              type: "dostava",
            },
          ],
        },
        {
          // Zamijeni <FIRM_ID> firmom koja NIJE internal:1 za ulogovani restoran
          // (npr. eksterna veza, internal: 0) — očekivano 403.
          label: "C8 GET eksterna firma <FIRM_ID> (→403)",
          method: "GET",
          url: "companies/<FIRM_ID>/users-delivery",
        },
      ],
    };
  },
  computed: {
    bodyAllowed() {
      return this.method !== "GET";
    },
    bodyError() {
      if (!this.bodyAllowed || !this.body.trim()) return false;
      try {
        JSON.parse(this.body);
        return false;
      } catch (e) {
        return true;
      }
    },
    hasToken() {
      return !!window.localStorage.getItem("restoran-token");
    },
    userType() {
      return this.$store.state.user.user && this.$store.state.user.user.type;
    },
    companyId() {
      return this.$store.state.company && this.$store.state.company.id;
    },
    restaurantId() {
      return this.$store.state.restaurant && this.$store.state.restaurant.id;
    },
    statusColor() {
      if (!this.response) return "inherit";
      const s = this.response.status;
      if (s >= 200 && s < 300) return "#2e7d32";
      if (s >= 400) return "#c62828";
      return "#ef6c00";
    },
    prettyBody() {
      if (!this.response) return "";
      try {
        return JSON.stringify(this.response.data, null, 2);
      } catch (e) {
        return String(this.response.data);
      }
    },
    prettyHeaders() {
      if (!this.response) return "";
      return JSON.stringify(this.response.headers, null, 2);
    },
  },
  created() {
    try {
      const saved = JSON.parse(window.localStorage.getItem(LS_KEY) || "null");
      if (saved) {
        this.method = saved.method || "GET";
        this.url = saved.url || "";
        this.body = saved.body || "";
      }
    } catch (e) {
      /* ignore */
    }
  },
  methods: {
    applyPreset(p) {
      this.method = p.method;
      this.url = p.url;
      this.body = p.body ? JSON.stringify(p.body, null, 2) : "";
    },
    restore(h) {
      this.method = h.method;
      this.url = h.url;
      this.body = h.body || "";
    },
    async send() {
      if (!this.url.trim()) return;
      if (this.bodyError) return;

      this.loading = true;
      this.response = null;
      this.errorText = "";

      const path = this.url.trim().replace(/^\//, "");
      let data;
      if (this.bodyAllowed && this.body.trim()) {
        data = JSON.parse(this.body);
      }

      window.localStorage.setItem(
        LS_KEY,
        JSON.stringify({ method: this.method, url: this.url, body: this.body })
      );

      const t0 = performance.now();
      try {
        const cfgReq = { method: this.method.toLowerCase(), url: path };
        if (data !== undefined) cfgReq.data = data;
        const res = await axiosInstance.request(cfgReq);
        this.setResponse(res, t0, true);
      } catch (err) {
        if (err.response) {
          this.setResponse(err.response, t0, false);
        } else {
          this.errorText =
            (err && err.message) ||
            "Nema odgovora (mreža / CORS / zahtjev prekinut).";
          this.pushHistory({ status: "ERR", ok: false, ms: Math.round(performance.now() - t0) });
        }
      } finally {
        this.loading = false;
      }
    },
    setResponse(res, t0, ok) {
      const ms = Math.round(performance.now() - t0);
      let size = "—";
      try {
        size = new Blob([JSON.stringify(res.data)]).size + " B";
      } catch (e) {
        /* ignore */
      }
      this.response = {
        status: res.status,
        statusText: res.statusText || "",
        ms,
        size,
        data: res.data,
        headers: res.headers || {},
      };
      this.pushHistory({ status: res.status, ok, ms });
    },
    pushHistory(extra) {
      this.history.unshift({
        method: this.method,
        url: this.url,
        body: this.body,
        ...extra,
      });
      if (this.history.length > 20) this.history.pop();
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        /* clipboard blocked — ignore */
      }
    },
  },
};
</script>

<style scoped>
.resp {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 12px;
  border-radius: 4px;
  overflow: auto;
  max-height: 480px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre;
}
.hist {
  font-size: 12px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  cursor: pointer;
}
.hist:hover {
  background: rgba(0, 0, 0, 0.03);
}
code {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 4px;
  border-radius: 3px;
}
</style>
